<script lang="ts">
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import { submitContact, submitNewsletter, LeadApiError } from '$lib/api/leads';

  type FormState = 'idle' | 'submitting' | 'success' | 'error';

  type Cta = { label: string; href: string };
  interface Props {
    headline?: string;
    subhead?: string;
    primaryCta?: Cta;
    secondaryCta?: Cta;
    contactButtonLabel?: string;
  }

  let {
    headline = 'Need help or guidance?',
    subhead = "We're here. Contact us for any reason — whether you're evaluating Corral, planning a deployment, or just have questions about enterprise AI.",
    primaryCta,
    secondaryCta,
    contactButtonLabel = 'Contact us',
  }: Props = $props();

  let expanded = $state(false);
  let formState = $state<FormState>('idle');
  let errorMessage = $state('');

  // Form fields
  let email = $state('');
  let name = $state('');
  let subject = $state('');
  let message = $state('');

  // Validation errors
  let emailError = $state('');
  let messageError = $state('');

  // Newsletter opt-in
  let subscribeNewsletter = $state(false);

  function validateForm(): boolean {
    let valid = true;

    // Email - required + format
    if (!email.trim()) {
      emailError = 'Email is required';
      valid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        emailError = 'Please enter a valid email address';
        valid = false;
      } else {
        emailError = '';
      }
    }

    // Message - required
    if (!message.trim()) {
      messageError = 'Message is required';
      valid = false;
    } else {
      messageError = '';
    }

    return valid;
  }

  function toggleForm() {
    if (formState === 'success') {
      // Reset for new submission
      formState = 'idle';
      email = '';
      name = '';
      subject = '';
      message = '';
      emailError = '';
      messageError = '';
      subscribeNewsletter = false;
    }
    expanded = !expanded;
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    formState = 'submitting';
    errorMessage = '';

    try {
      // Submit contact form
      await submitContact({
        Email: email.trim(),
        Name: name.trim() || undefined,
        Subject: subject.trim() || undefined,
        Message: message.trim()
      });

      // Also subscribe to newsletter if opted in
      if (subscribeNewsletter) {
        await submitNewsletter(email.trim());
      }

      formState = 'success';
    } catch (err) {
      formState = 'error';
      if (err instanceof LeadApiError) {
        errorMessage = err.message;
      } else {
        errorMessage = 'Something went wrong. Please try again.';
      }
    }
  }

  function resetForm() {
    formState = 'idle';
    errorMessage = '';
  }
</script>

<section class="bg-black py-[80px] relative">
  <GridLines variant="white" />

  <!-- Top horizontal line -->
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
  <!-- Bottom-half stars facing down at top line -->
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />

  <!-- Top-half stars facing up at bottom edge (will form full stars with footer's bottom-half stars) -->
  <Star variant="top" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-0" />
  <Star variant="top" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-0" />

  <GridContent>
    <div class="max-w-[840px] mx-auto text-center flex flex-col items-center justify-center">
        <h2 class="font-display font-[300] text-[40px] md:text-[52px] lg:text-[60px] text-white tracking-[-0.035em] leading-[1.02] mb-6">
          {headline}
        </h2>
        <p class="font-body font-light text-[16px] lg:text-[18px] text-[#878787] leading-[1.55] mb-10 max-w-[640px]">
          {subhead}
        </p>

        <!-- CTA row: primary + secondary + tertiary contact button -->
        <div class="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
          {#if primaryCta}
            <a
              href={primaryCta.href}
              class="bg-[#ee6018] text-white font-mono text-[14px] h-[52px] px-8 flex items-center justify-center hover:bg-[#ff6e20] transition-colors"
              style="box-shadow: inset 0 0 14px 4px rgba(255, 255, 255, 0.28);"
            >
              {primaryCta.label}
            </a>
          {/if}

          {#if secondaryCta}
            <CornerFrame
              class="h-[52px] inline-flex"
              bg="bg-transparent"
              borderColor="#656565"
              cornerColor="white"
              cornerSize={7}
            >
              <a
                href={secondaryCta.href}
                class="w-full h-full px-8 flex items-center justify-center font-mono text-[14px] text-white hover:bg-white/10 transition-colors"
              >
                {secondaryCta.label}
              </a>
            </CornerFrame>
          {/if}

          <!-- Contact/Talk-to-us button — CornerFrame if no other CTAs, else text link -->
          {#if primaryCta || secondaryCta}
            <button
              type="button"
              onclick={toggleForm}
              class="font-mono text-[14px] text-[#bcbcbc] hover:text-white inline-flex items-center gap-1.5 transition-colors cursor-pointer h-[52px] px-2"
            >
              {#if expanded && formState !== 'success'}
                Close
              {:else if formState === 'success'}
                Send another message
              {:else}
                {contactButtonLabel}
              {/if}
              <span aria-hidden="true" class="text-[#ee6018]">→</span>
            </button>
          {:else}
            <CornerFrame
              class="h-[44px] inline-flex"
              bg="bg-transparent"
              borderColor="#656565"
              cornerColor="white"
              cornerSize={7}
            >
              <button
                type="button"
                onclick={toggleForm}
                class="w-full h-full px-8 flex items-center justify-center font-sans font-medium text-[16px] text-white hover:bg-white/10 transition-colors cursor-pointer"
              >
                {#if expanded && formState !== 'success'}
                  Close
                {:else if formState === 'success'}
                  Send another message
                {:else}
                  {contactButtonLabel}
                {/if}
              </button>
            </CornerFrame>
          {/if}
        </div>

        <!-- Expandable Form Container -->
        <div
          class="grid transition-[grid-template-rows] duration-300 ease-out w-full max-w-[600px]"
          style="grid-template-rows: {expanded ? '1fr' : '0fr'};"
        >
          <div class="overflow-hidden">
            <div class="pt-10">
              {#if formState === 'success'}
                <!-- Success State -->
                <div class="text-center py-8">
                  <div class="font-display text-[24px] text-white mb-3">
                    Message sent!
                  </div>
                  <p class="font-mono font-light text-[14px] text-[#878787]">
                    We'll get back to you soon.
                  </p>
                </div>
              {:else}
                <!-- Contact Form -->
                <form onsubmit={handleSubmit} class="space-y-5">
                  {#if formState === 'error' && errorMessage}
                    <div class="bg-red-500/10 border border-red-500/30 px-4 py-3 text-left">
                      <p class="font-mono text-[14px] text-red-400">{errorMessage}</p>
                      <button
                        type="button"
                        onclick={resetForm}
                        class="font-mono text-[12px] text-red-400/70 hover:text-red-400 underline mt-1"
                      >
                        Dismiss
                      </button>
                    </div>
                  {/if}

                  <!-- Email (Required) -->
                  <div class="text-left">
                    <label for="contact-email" class="font-mono text-[12px] text-[#878787] uppercase tracking-wider block mb-2">
                      Email <span class="text-white">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      bind:value={email}
                      required
                      disabled={formState === 'submitting'}
                      class="w-full bg-white/5 border border-[#656565] px-4 py-3 font-body text-[16px] text-white placeholder:text-[#656565] focus:outline-none focus:border-white/50 transition-colors disabled:opacity-50"
                      placeholder="your@email.com"
                    />
                    {#if emailError}
                      <p class="font-mono text-[12px] text-red-400 mt-1">{emailError}</p>
                    {/if}
                  </div>

                  <!-- Name (Optional) -->
                  <div class="text-left">
                    <label for="contact-name" class="font-mono text-[12px] text-[#878787] uppercase tracking-wider block mb-2">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      bind:value={name}
                      disabled={formState === 'submitting'}
                      class="w-full bg-white/5 border border-[#656565] px-4 py-3 font-body text-[16px] text-white placeholder:text-[#656565] focus:outline-none focus:border-white/50 transition-colors disabled:opacity-50"
                      placeholder="Your name"
                    />
                  </div>

                  <!-- Subject (Optional) -->
                  <div class="text-left">
                    <label for="contact-subject" class="font-mono text-[12px] text-[#878787] uppercase tracking-wider block mb-2">
                      Subject
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      bind:value={subject}
                      disabled={formState === 'submitting'}
                      class="w-full bg-white/5 border border-[#656565] px-4 py-3 font-body text-[16px] text-white placeholder:text-[#656565] focus:outline-none focus:border-white/50 transition-colors disabled:opacity-50"
                      placeholder="What's this about?"
                    />
                  </div>

                  <!-- Message (Required) -->
                  <div class="text-left">
                    <label for="contact-message" class="font-mono text-[12px] text-[#878787] uppercase tracking-wider block mb-2">
                      Message <span class="text-white">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      bind:value={message}
                      required
                      disabled={formState === 'submitting'}
                      rows={4}
                      class="w-full bg-white/5 border border-[#656565] px-4 py-3 font-body text-[16px] text-white placeholder:text-[#656565] focus:outline-none focus:border-white/50 transition-colors disabled:opacity-50 resize-none"
                      placeholder="How can we help?"
                    ></textarea>
                    {#if messageError}
                      <p class="font-mono text-[12px] text-red-400 mt-1">{messageError}</p>
                    {/if}
                  </div>

                  <!-- Newsletter Opt-in -->
                  <div class="text-left">
                    <label class="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        bind:checked={subscribeNewsletter}
                        disabled={formState === 'submitting'}
                        class="mt-1 w-4 h-4 bg-white/5 border border-[#656565] rounded-none appearance-none checked:bg-white checked:border-white cursor-pointer disabled:opacity-50"
                      />
                      <span class="font-mono text-[13px] text-[#878787] leading-relaxed group-hover:text-[#a0a0a0] transition-colors">
                        Sign up for our newsletter? Once a week max, no spam. Just keeps you updated with what we're doing.
                      </span>
                    </label>
                  </div>

                  <!-- Submit Button -->
                  <div class="pt-2">
                    <CornerFrame
                      class="h-[48px] inline-flex {formState === 'submitting' ? 'opacity-50' : ''}"
                      bg="bg-transparent"
                      borderColor="#656565"
                      cornerColor="white"
                      cornerSize={7}
                    >
                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        class="w-full h-full px-10 flex items-center justify-center font-sans font-medium text-[16px] text-white hover:bg-white/10 transition-colors {formState === 'submitting' ? 'cursor-not-allowed' : 'cursor-pointer'}"
                      >
                        {#if formState === 'submitting'}
                          Sending...
                        {:else}
                          Send message
                        {/if}
                      </button>
                    </CornerFrame>
                  </div>
                </form>
              {/if}
            </div>
          </div>
        </div>
    </div>
  </GridContent>
</section>
